import { ReactNode, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import {
    createColumnHelper,
    flexRender,
    getCoreRowModel,
    getExpandedRowModel,
    useReactTable,
} from '@tanstack/react-table';
import { Button } from '@givebutter/primitives/button';
import { useToast } from '@givebutter/primitives/toast';
import { useMediaQuery } from '@givebutter/react-util';
import { faCheck, faChevronDown, faChevronRight } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    FetchTransactionItemsParams,
    fetchTransactionItems,
    toggleFulfillTransactionItem,
} from 'api/transactions';
import * as DropdownMenu from 'components/dropdown-menu';
import { CopyContactInfo } from 'components/pages/campaign-settings/campaign-auction/CopyContactInfo';
import {
    ItemPaymentDetails,
    ItemPaymentDetailsMini,
} from 'components/pages/campaign-settings/campaign-auction/ItemPaymentDetails';
import { TableConstants, sortQuery, useReactTableState } from 'components/table';
import { useSyncPaginatedData } from 'components/table/useSyncPaginatedData';
import { TruncatedText } from 'components/truncated-text';
import { EmptyData } from 'components/util';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { useCurrentCampaign } from 'customHooks/useCurrentCampaign';
import useDebounce from 'customHooks/useDebounce';
import { useIsPending } from 'customHooks/useIsPending';
import { usePersistentSearch } from 'customHooks/usePersistentSearch';
import { DEFAULT_DATE_FORMAT, dateFromServer } from 'helpers/dates';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { transactionItemQueryKeys } from 'queries/transaction-item-query-keys';
import { TransactionLineItem } from 'types/domain';

type PaidItemsFilter = 'all' | 'fulfilled' | 'unfulfilled';

const columnHelper = createColumnHelper<TransactionLineItem>();

const ColumnIds = {
    ExpandAction: TableConstants.Expand,
    Description: 'description',
    ItemId: 'item-id',
    ItemDetails: 'item-details',
    Winner: 'winner',
    TransactionNumber: 'transaction-number',
    PaidDate: 'paid-date',
    FulfillmentAction: 'fulfillment-action',
};

const useItemFulfillment = () => {
    const toast = useToast();
    const queryClient = useQueryClient();
    const { accountId } = useCurrentAccount();
    const { data: campaign } = useCurrentCampaign();
    const {
        expanded,
        pagination,
        setPagination,
        sorting,
        setSorting,
        setExpanded,
        columnVisibility,
        setColumnVisibility,
    } = useReactTableState();
    const [paidItemsFilter, setPaidItemsFilter] = useState<PaidItemsFilter>('all');
    const isMobile = useMediaQuery(`(max-width: 640px)`);
    const campaignId = campaign.id;
    const campaignParam = [campaignId];
    const { auctionsManageBidder } = useFlags();

    const [search, setSearch] = usePersistentSearch();
    const searchTerm = useDebounce(search);

    const [isPending, transactionFetchParams] = useIsPending(
        {
            type: 'item',
            page: pagination.pageIndex + 1,
            per_page: pagination.pageSize,
            campaigns: campaignParam,
            auctions: [campaign.auction_id!],
            fulfilled: transformPaidItemFilterToRequest(paidItemsFilter),
            query: searchTerm ?? '',
            ...sortQuery(sorting),
        } as Omit<FetchTransactionItemsParams, 'accountId'>,
        500,
    );

    useEffect(() => {
        if (isMobile) {
            setColumnVisibility({
                [ColumnIds.Description]: false,
                [ColumnIds.Winner]: false,
                [ColumnIds.TransactionNumber]: false,
                [ColumnIds.PaidDate]: false,
            });
        } else {
            setColumnVisibility({
                [ColumnIds.ItemDetails]: false,
            });
        }
    }, [isMobile, setColumnVisibility]);

    const { data } = useSuspenseQuery({
        queryKey: transactionItemQueryKeys.filters(accountId, transactionFetchParams),
        queryFn: () =>
            fetchTransactionItems({
                accountId,
                ...transactionFetchParams,
            }),
    });

    const {
        isPending: isLoading,
        mutate: toggleFulfill,
        variables: fulfillParams,
    } = useMutation({
        mutationFn: (item: TransactionLineItem) =>
            toggleFulfillTransactionItem(!item.fulfilled_at)({
                accountId,
                transactionId: item.transaction.id,
                itemId: item.id,
            }),
        onSuccess: (_, params) => {
            const text = params.fulfilled_at ? 'unfulfilled' : 'fulfilled';
            queryClient.invalidateQueries({
                queryKey: transactionItemQueryKeys.campaigns(accountId, campaignParam),
            });
            toast.success(`Marked item as ${text}!`);
        },
        onError: () => {
            toast.error('Unable to update item.');
        },
    });

    const fulfillId = fulfillParams?.id;

    const columns = useMemo(() => {
        return [
            columnHelper.display({
                id: ColumnIds.ExpandAction,
                cell: info => {
                    const isExpanded = info.row.getIsExpanded();

                    return (
                        <Button
                            variant="none"
                            color="neutral"
                            aria-expanded={isExpanded}
                            aria-label="Toggle Auction Item Info"
                            onClick={() => {
                                info.row.toggleExpanded(!isExpanded);
                            }}
                        >
                            <FontAwesomeIcon
                                className={`${isExpanded ? 'rotate-90' : ''}`}
                                icon={faChevronRight}
                            />
                        </Button>
                    );
                },
                meta: {
                    colSpan: 1,
                    className: 'pr-0 w-8',
                },
            }),
            columnHelper.accessor('item', {
                id: ColumnIds.ItemDetails,
                header: undefined,
                cell: info => {
                    return <ItemPaymentDetailsMini item={info.getValue()} />;
                },
                meta: {
                    colSpan: 3,
                },
            }),
            columnHelper.accessor('description', {
                id: ColumnIds.Description,
                header: 'Item',
                cell: info => {
                    const auctionItem = info.row.original.item;
                    const itemName = info.getValue() || auctionItem?.name;

                    return (
                        <div className="flex max-w-[12rem] font-medium">
                            <TruncatedText>{itemName}</TruncatedText>
                        </div>
                    );
                },
            }),
            columnHelper.accessor('item.id', {
                id: ColumnIds.ItemId,
                enableSorting: false,
                header: 'Item #',
                cell: info => {
                    return (
                        <div className="flex max-w-[12rem] font-medium">
                            <TruncatedText>#{info.getValue()}</TruncatedText>
                        </div>
                    );
                },
            }),
            columnHelper.accessor('transaction.contact', {
                id: ColumnIds.Winner,
                enableSorting: false,
                header: 'Winner',
                cell: info => {
                    const contact = info.getValue();
                    if (!contact) return <EmptyData />;
                    const contactName = contact.name_display;

                    return auctionsManageBidder ? (
                        <Link
                            to={`/accounts/${accountId}/campaigns/${campaignId}/auction/bidders/${contact.id}/edit`}
                            className="text-blue-600 hover:underline"
                        >
                            {contactName}
                        </Link>
                    ) : (
                        <span className="font-medium">{contactName}</span>
                    );
                },
            }),
            columnHelper.accessor('transaction.number', {
                id: ColumnIds.TransactionNumber,
                enableSorting: false,
                header: 'Transaction #',
                cell: info => {
                    return <span>{info.getValue()}</span>;
                },
            }),
            columnHelper.accessor('transaction.captured_at', {
                id: ColumnIds.PaidDate,
                enableSorting: false,
                header: 'Paid date',
                cell: info => {
                    const { transaction } = info.row.original;
                    const transactionDate = transaction.captured_at || transaction.transacted_at;

                    if (!transactionDate) {
                        return <EmptyData />;
                    }

                    return (
                        <span>{dateFromServer(transactionDate).toFormat(DEFAULT_DATE_FORMAT)}</span>
                    );
                },
            }),
            columnHelper.display({
                id: ColumnIds.FulfillmentAction,
                cell: info => {
                    const isDisabled = isLoading && fulfillId === info.row.original.id;

                    return (
                        <div
                            className={`divide float-right flex w-fit items-stretch divide-x divide-gray-300 rounded-md border border-gray-300 ${
                                isDisabled ? 'pointer-events-none opacity-70' : ''
                            }`}
                        >
                            <div className={'px-3 py-1'}>
                                {info.row.original.fulfilled_at ? (
                                    <div className={'flex items-center gap-x-2 text-green-500'}>
                                        <span>Fulfilled</span>
                                        <FontAwesomeIcon icon={faCheck} />
                                    </div>
                                ) : (
                                    <span>Unfulfilled</span>
                                )}
                            </div>
                            <DropdownMenu.Root>
                                <DropdownMenu.Trigger className={'px-2'}>
                                    <FontAwesomeIcon icon={faChevronDown} />
                                </DropdownMenu.Trigger>
                                <DropdownMenu.Content align={'end'}>
                                    <DropdownMenu.Item
                                        onClick={() => toggleFulfill(info.row.original)}
                                    >
                                        Mark item{' '}
                                        {info.row.original.fulfilled_at
                                            ? 'unfulfilled'
                                            : 'fulfilled'}
                                    </DropdownMenu.Item>
                                </DropdownMenu.Content>
                            </DropdownMenu.Root>
                        </div>
                    );
                },
                meta: {
                    colSpan: 3,
                    className: 'py-0 pl-0',
                },
            }),
        ];
    }, [accountId, auctionsManageBidder, campaignId, fulfillId, isLoading, toggleFulfill]);

    const table = useReactTable({
        data: data?.data ?? [],
        columns,
        pageCount: data?.meta.last_page ?? -1,
        getCoreRowModel: getCoreRowModel(),
        getExpandedRowModel: getExpandedRowModel(),
        state: {
            pagination,
            expanded,
            columnVisibility,
        },
        onPaginationChange: setPagination,
        onSortingChange: setSorting,
        onExpandedChange: setExpanded,
        manualPagination: true,
        enableSorting: true,
        enableHiding: false,
        enableColumnPinning: false,
        meta: {
            colSpan: isMobile ? 6 : undefined,
            renderExpandedRow: row => {
                const getCellById = (id: string) =>
                    row.getAllCells().find(cell => cell.column.id === id);
                const [winnerCell, transactionCell, paidDateCell] = [
                    ColumnIds.Winner,
                    ColumnIds.TransactionNumber,
                    ColumnIds.PaidDate,
                ].map(getCellById);

                return (
                    <tr>
                        <td
                            className={'w-min bg-white px-4 pb-4'}
                            colSpan={table.options.meta?.colSpan ?? row.getVisibleCells().length}
                        >
                            <div className={'grid grid-cols-2 gap-4 sm:gap-8'}>
                                <div className={'col-span-full flex justify-between sm:hidden'}>
                                    {winnerCell && (
                                        <div className={'flex flex-col gap-y-2'}>
                                            <p className={'text-xs text-gray-500'}>
                                                {winnerCell.column.columnDef.header as ReactNode}
                                            </p>
                                            <p className={'text-sm font-medium'}>
                                                {flexRender(
                                                    winnerCell.column.columnDef.cell,
                                                    winnerCell.getContext(),
                                                )}
                                            </p>
                                        </div>
                                    )}
                                    {transactionCell && (
                                        <div className={'flex flex-col gap-y-2'}>
                                            <p className={'text-xs text-gray-500'}>
                                                {
                                                    transactionCell.column.columnDef
                                                        .header as ReactNode
                                                }
                                            </p>
                                            <p className={'text-sm font-medium'}>
                                                {flexRender(
                                                    transactionCell.column.columnDef.cell,
                                                    transactionCell.getContext(),
                                                )}
                                            </p>
                                        </div>
                                    )}
                                    {paidDateCell && (
                                        <div className={'flex flex-col gap-y-2'}>
                                            <p className={'text-xs text-gray-500'}>
                                                {paidDateCell.column.columnDef.header as ReactNode}
                                            </p>
                                            <p className={'text-sm font-medium'}>
                                                {flexRender(
                                                    paidDateCell.column.columnDef.cell,
                                                    paidDateCell.getContext(),
                                                )}
                                            </p>
                                        </div>
                                    )}
                                </div>
                                <div
                                    className={
                                        'order-last col-span-full flex flex-col gap-y-4 sm:order-none md:col-span-1'
                                    }
                                >
                                    <ItemPaymentDetails
                                        className={'hidden sm:flex'}
                                        item={{
                                            ...row.original.item!,
                                            price: row.original.price,
                                        }}
                                    />
                                    <div className={'flex justify-end'}>
                                        <Link
                                            to={`/accounts/${accountId}/transactions/${row.original.transaction.id}`}
                                            className={'text-sm text-blue-500'}
                                        >
                                            View Transaction
                                        </Link>
                                    </div>
                                </div>
                                <CopyContactInfo
                                    phone={row.original.transaction.phone}
                                    address={row.original.transaction.shipping_address}
                                    email={row.original.transaction.email}
                                    className={'col-span-full flex flex-col gap-y-4 md:col-span-1'}
                                />
                            </div>
                        </td>
                    </tr>
                );
            },
        },
    });

    useSyncPaginatedData(table, data);

    return {
        accountId,
        paidItemsFilter,
        setPaidItemsFilter,
        table,
        search,
        setSearch,
        searchTerm,
        isPending,
    };
};

const transformPaidItemFilterToRequest = (filter: PaidItemsFilter) => {
    switch (filter) {
        case 'all':
            return undefined;
        case 'fulfilled':
            return true;
        case 'unfulfilled':
            return false;
    }
};

export { useItemFulfillment, ColumnIds };
export type { PaidItemsFilter };
